import { Box, Center, Flex, Image, useToast } from '@chakra-ui/react'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import React from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { PrimaryButton } from '../../components/chakra-v2/PrimaryButton'
import { FormErrorMessage } from '../../components/FormErrorMessage'
import { FormInput } from '../../components/FormInput'
import { OnboardingUser } from '../../interfaces'
import {
  SinglePageSignupComponent,
  useUserDetails
} from '../../context/user-onboard-context'
import { EMAIL_REGEX } from '../../utils/constants'
import { OnboardingHeading } from '../Onboarding/OnboardingComponents'
import { Phone } from '../../styled/Phone'
import { usePlatformTenant } from '../../context/platform-tenant-context'
import { CenteredSpinner } from '../../styled/CenteredSpinner'
import { sendEmailVerificationToken } from '../../api'

export const ExternalOnboardingEmail: React.FC<SinglePageSignupComponent> = (
  props
) => {
  const toast = useToast()
  const { onCompleteCallback, setComplete, setIsActive } = props
  const { platformTheme } = usePlatformTenant()

  const { setUserDetails, userDetails } = useUserDetails()

  function lowerEmail(input) {
    input.email = input.email.toLowerCase()
    return input
  }
  if (!platformTheme) return <CenteredSpinner />
  return (
    <Box margin={'0 auto'} w={'100%'} maxW="450px" onClick={setIsActive}>
      <Center mb={1}>
        <Image
          src={platformTheme.logo_long}
          w={'10rem'}
          alt={`${platformTheme.business_name} Logo`}
        />
      </Center>
      <OnboardingHeading
        size={'xs'}
        color={'gray.400'}
        mb={4}
        textAlign="center"
      >
        Book a space at {platformTheme?.business_name}
      </OnboardingHeading>
      <OnboardingHeading
        size={'md'}
        color={'gray.800'}
        mb={4}
        textAlign="center"
      >
        Let's start with your details
      </OnboardingHeading>

      <Formik
        initialValues={{
          email: userDetails.email,
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          mobileNumber: userDetails.mobileNumber
        }}
        validate={(values): OnboardingUser => {
          const errors: any = {}

          if (!values.email) {
            errors.email = 'Required'
          } else if (!EMAIL_REGEX.test(values.email)) {
            errors.email = 'Invalid email address'
          }
          if (!values.firstName) {
            errors.firstName = 'Required'
          }
          if (!values.lastName) {
            errors.lastName = 'Required'
          }
          if (!values.mobileNumber) {
            errors.mobileNumber = 'Required'
          } else {
            if (!isValidPhoneNumber(values.mobileNumber)) {
              errors.mobileNumber = 'Invalid phone number'
            }
          }
          if (setComplete !== undefined) {
            setComplete(false)
          }
          return errors
        }}
        onSubmit={(values, { setSubmitting }) => {
          setUserDetails((currentValues: any) => ({
            ...currentValues,
            ...lowerEmail(values)
          }))
          sendEmailVerificationToken(values.email)
            .then((res) => {
              setSubmitting(false)
              if (!res.data) {
                toast({
                  title: 'The email address is already registered',
                  status: 'warning',
                  duration: 4000,
                  position: 'top'
                })
              } else {
                toast({
                  title: 'Verification code sent',
                  duration: 3000,
                  position: 'top'
                })
                onCompleteCallback()
              }
            })
            .catch((err) => {
              setSubmitting(false)
            })
        }}
      >
        {({ isSubmitting, touched, errors }) => (
          <Form>
            <Flex direction="column">
              <Box mb={8}>
                <Field
                  type="email"
                  name="email"
                  autoFocus={true}
                  isInvalid={touched.email && errors.email !== undefined}
                  placeholder="Email"
                  component={FormInput}
                  data-testid="onboarding-email-field"
                />
                <ErrorMessage name="email" component={FormErrorMessage} />
              </Box>
              <Flex
                mb={5}
                justifyContent={'space-between'}
                flexDir={['column', 'row']}
              >
                <Box w={['100%', '48%', '48%']}>
                  <Field
                    type="text"
                    name="firstName"
                    isInvalid={
                      touched.firstName && errors.firstName !== undefined
                    }
                    component={FormInput}
                    data-testid="onboarding-firstName-field"
                    placeholder="First Name"
                  />
                  <Box h={5}>
                    <ErrorMessage
                      name="firstName"
                      component={FormErrorMessage}
                    />
                  </Box>
                </Box>
                <Box w={['100%', '48%', '48%']}>
                  <Field
                    isInvalid={
                      touched.lastName && errors.lastName !== undefined
                    }
                    type="text"
                    name="lastName"
                    component={FormInput}
                    data-testid="onboarding-lastName-field"
                    placeholder="Last Name"
                  />
                  <Box h={5}>
                    <ErrorMessage
                      name="lastName"
                      component={FormErrorMessage}
                    />
                  </Box>
                </Box>
              </Flex>
              <Box mb={5}>
                <Field
                  isInvalid={
                    touched.mobileNumber && errors.mobileNumber !== undefined
                  }
                  data-testid="onboarding-mobileNumber-field"
                  name="mobileNumber"
                  placeholder="Mobile Number"
                  component={Phone}
                />
                <ErrorMessage
                  name="mobileNumber"
                  component={FormErrorMessage}
                />
              </Box>
              <PrimaryButton
                type="submit"
                px={6}
                data-testid="onboarding-email-next"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                loadingText={'Loading'}
                flexGrow={0}
                mr="auto"
                ml="auto"
              >
                Continue
              </PrimaryButton>
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
